import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import HarvestFormDialog from "@/components/HarvestFormDialog";
import { Plus, Pencil, Trash2, Droplets, Trophy, TrendingUp, TrendingDown, Minus, BellRing } from "lucide-react";
import { format, parseISO, isPast } from "date-fns";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { useAuth } from "@/context/AuthContext";
import { formatWeight, convertWeight } from "@/lib/format";

const PRODUCT_COLORS = {
  honey: "bg-amber-100 text-amber-900",
  wax: "bg-yellow-100 text-yellow-800",
  propolis: "bg-stone-200 text-stone-800",
  pollen: "bg-orange-100 text-orange-800",
};

export default function Harvests() {
  const { user: prefs } = useAuth();
  const [year, setYear] = useState(new Date().getFullYear());
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [hives, setHives] = useState([]);
  const [years, setYears] = useState([]);
  const [yoy, setYoy] = useState(null);
  const [dueEvents, setDueEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [presetEvent, setPresetEvent] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [s, e, h, y, yy, ev] = await Promise.all([
      api.get("/harvests/summary", { params: { year } }),
      api.get("/harvests", { params: { year } }),
      api.get("/hives"),
      api.get("/harvests/years"),
      api.get("/harvests/yoy", { params: { years: 3 } }),
      api.get("/events"),
    ]);
    setSummary(s.data);
    setEntries(e.data);
    setHives(h.data);
    setYears(y.data);
    setYoy(yy.data);
    setDueEvents(
      ev.data.filter(
        (x) =>
          x.event_type === "harvest" &&
          !x.completed &&
          new Date(x.due_date).getFullYear() === year,
      ),
    );
  };

  useEffect(() => { load(); }, [year]);

  const del = async (h) => {
    if (!confirm("Delete this harvest entry?")) return;
    await api.delete(`/harvests/${h.id}`);
    load();
  };

  const availableYears = useMemo(() => {
    const cur = new Date().getFullYear();
    const base = new Set([cur, cur - 1, cur - 2, year, ...years]);
    return Array.from(base).sort((a, b) => b - a);
  }, [years, year]);

  const maxHive = summary?.by_hive?.[0]?.total_lb || 1;

  return (
    <div className="space-y-8" data-testid="harvests-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">Yield</div>
          <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tight mt-2">Harvests</h1>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="rounded-full bg-white w-32 h-10" data-testid="year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableYears.map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setPresetEvent(null); }}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300" data-testid="add-harvest-button" onClick={() => setPresetEvent(null)}>
                <Plus className="w-4 h-4 mr-2" /> Log harvest
              </Button>
            </DialogTrigger>
            {open && (
              <HarvestFormDialog
                key={presetEvent?.id || "new"}
                hives={hives}
                defaultYear={year}
                event={presetEvent}
                onSaved={() => { setOpen(false); setPresetEvent(null); load(); }}
              />
            )}
          </Dialog>
          <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
            {editing && (
              <HarvestFormDialog
                key={editing.id}
                hives={hives}
                harvest={editing}
                onSaved={() => { setEditing(null); load(); }}
              />
            )}
          </Dialog>
        </div>
      </div>

      {dueEvents.length > 0 && (
        <DueHarvests
          events={dueEvents}
          hives={hives}
          onPick={(ev) => { setPresetEvent(ev); setOpen(true); }}
        />
      )}

      <div className="grid sm:grid-cols-3 gap-4">
        <StatCard label={`Total ${year}`} value={formatWeight(summary?.total_lb, prefs)} icon={Droplets} tint="amber" testid="stat-total" />
        <StatCard label="Honey" value={formatWeight(summary?.honey_lb, prefs)} icon={Droplets} tint="yellow" testid="stat-honey" />
        <StatCard label="Hives harvested" value={summary?.by_hive?.length ?? 0} icon={Trophy} tint="stone" testid="stat-hive-count" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-stone-200 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-4 h-4 text-amber-600" />
            <h2 className="font-display text-xl font-medium">Top producers · {year}</h2>
          </div>
          {!summary?.by_hive?.length ? (
            <div className="text-sm text-stone-500 py-8 text-center">
              No harvests logged for {year}.
            </div>
          ) : (
            <ul className="space-y-3" data-testid="top-producers">
              {summary.by_hive.map((row, i) => {
                const pct = Math.round((row.total_lb / maxHive) * 100);
                return (
                  <li key={row.hive_id} data-testid={`producer-${row.hive_id}`}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <div className="flex items-center gap-2">
                        <span className={`w-6 h-6 rounded-full grid place-items-center text-xs font-medium ${i === 0 ? "bg-yellow-400 text-amber-900" : i === 1 ? "bg-stone-200 text-stone-700" : i === 2 ? "bg-amber-200 text-amber-900" : "bg-stone-100 text-stone-600"}`}>
                          {i + 1}
                        </span>
                        <span className="font-medium text-stone-800">{row.hive_name}</span>
                      </div>
                      <span className="font-display font-medium">{formatWeight(row.total_lb, prefs)}</span>
                    </div>
                    <div className="h-2 rounded-full bg-stone-100 overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-yellow-300 to-amber-500" style={{ width: `${pct}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="bg-white border border-stone-200 rounded-2xl p-6">
          <h2 className="font-display text-xl font-medium mb-4">Entries · {year}</h2>
          {entries.length === 0 ? (
            <div className="text-sm text-stone-500 py-8 text-center">
              Log your first pull to track season totals.
            </div>
          ) : (
            <ul className="divide-y divide-stone-100" data-testid="harvest-entries">
              {entries.map((h) => {
                const hiveName = hives.find((x) => x.id === h.hive_id)?.name || "Hive";
                return (
                  <li key={h.id} className="py-3 flex items-center gap-3" data-testid={`harvest-${h.id}`}>
                    <span className={`text-xs px-2 py-1 rounded-full ${PRODUCT_COLORS[h.product_type] || PRODUCT_COLORS.honey}`}>
                      {h.product_type}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{hiveName}</div>
                      <div className="text-xs text-stone-500">
                        {h.harvest_date ? format(parseISO(h.harvest_date), "MMM d, yyyy") : `Year ${h.year}`}
                        {h.notes ? ` · ${h.notes}` : ""}
                      </div>
                    </div>
                    <div className="font-display text-lg tabular-nums">{formatWeight(h.amount_lb, prefs, { noUnit: true })}<span className="text-xs text-stone-500 font-sans ml-1">{prefs?.weight_unit || "lb"}</span></div>
                    <button onClick={() => setEditing(h)} className="text-stone-400 hover:text-stone-700" title="Edit" data-testid={`harvest-edit-${h.id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => del(h)} className="text-stone-400 hover:text-red-600" title="Delete" data-testid={`harvest-delete-${h.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <YoYChart yoy={yoy} prefs={prefs} />
    </div>
  );
}

function YoYChart({ yoy, prefs }) {
  if (!yoy || !yoy.by_hive?.length) return null;
  const [prev, curr] = [yoy.years[yoy.years.length - 2], yoy.years[yoy.years.length - 1]];
  const unit = prefs?.weight_unit || "lb";

  const chartData = yoy.by_hive.map((row) => {
    const obj = { hive: row.hive_name };
    yoy.years.forEach((y, i) => { obj[String(y)] = Number(convertWeight(row.totals[i], prefs).toFixed(1)); });
    return obj;
  });

  const YEAR_COLORS = ["#D6D3D1", "#FCD34D", "#F59E0B"]; // stone-300, amber-300, amber-500

  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-6" data-testid="yoy-chart">
      <div className="flex items-center gap-2 mb-1">
        <TrendingUp className="w-4 h-4 text-amber-600" />
        <h2 className="font-display text-xl font-medium">Season vs. season</h2>
      </div>
      <p className="text-sm text-stone-500 mb-6">{yoy.years[0]} — {yoy.years[yoy.years.length - 1]} totals per hive.</p>

      <div className="h-72 -ml-2">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" vertical={false} />
            <XAxis dataKey="hive" tickLine={false} axisLine={false} tick={{ fill: "#57534E", fontSize: 12 }} />
            <YAxis tickLine={false} axisLine={false} tick={{ fill: "#57534E", fontSize: 12 }} unit={` ${unit}`} width={60} />
            <Tooltip
              contentStyle={{ borderRadius: 12, border: "1px solid #E7E5E4", fontSize: 12 }}
              formatter={(v) => [`${v} ${unit}`, ""]}
              cursor={{ fill: "#FEF3C7", opacity: 0.3 }}
            />
            <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
            {yoy.years.map((y, i) => (
              <Bar key={y} dataKey={String(y)} fill={YEAR_COLORS[i] || "#A8A29E"} radius={[6, 6, 0, 0]} maxBarSize={50} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-6 pt-6 border-t border-stone-100">
        <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-3">
          {prev} → {curr} change
        </div>
        <ul className="grid sm:grid-cols-2 gap-2" data-testid="yoy-deltas">
          {yoy.by_hive.map((row) => {
            const n = yoy.years.length;
            const prevVal = convertWeight(row.totals[n - 2] || 0, prefs);
            const currVal = convertWeight(row.totals[n - 1] || 0, prefs);
            const diff = currVal - prevVal;
            const pct = prevVal > 0 ? Math.round((diff / prevVal) * 100) : null;
            const up = diff > 0;
            const flat = Math.abs(diff) < 0.01;
            const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
            const tone = flat ? "text-stone-500 bg-stone-100" : up ? "text-emerald-700 bg-emerald-50" : "text-orange-700 bg-orange-50";
            return (
              <li key={row.hive_id} className="flex items-center justify-between py-2 px-3 rounded-xl border border-stone-100" data-testid={`yoy-row-${row.hive_id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-stone-800 truncate">{row.hive_name}</div>
                  <div className="text-xs text-stone-500 tabular-nums">{prevVal.toFixed(1)} → {currVal.toFixed(1)} {unit}</div>
                </div>
                <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full ${tone}`}>
                  <Icon className="w-3 h-3" />
                  {flat ? "Flat" : `${up ? "+" : ""}${diff.toFixed(1)} ${unit}${pct !== null ? ` · ${up ? "+" : ""}${pct}%` : ""}`}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tint, testid }) {
  const tints = {
    amber: "text-amber-700",
    yellow: "text-yellow-600",
    stone: "text-stone-600",
  };
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.2em] text-stone-500">{label}</div>
        <Icon className={`w-4 h-4 ${tints[tint]}`} />
      </div>
      <div className="font-display text-4xl font-light mt-3">{value}</div>
    </div>
  );
}

function DueHarvests({ events, hives, onPick }) {
  const hiveName = (id) => hives.find((h) => h.id === id)?.name;
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5" data-testid="due-harvests">
      <div className="flex items-center gap-2 mb-3">
        <BellRing className="w-4 h-4 text-amber-700" />
        <h2 className="font-display text-lg font-medium text-amber-900">Harvest reminders waiting</h2>
        <span className="text-xs text-amber-800">({events.length})</span>
      </div>
      <ul className="space-y-2">
        {events.map((ev) => {
          const overdue = isPast(parseISO(ev.due_date));
          return (
            <li
              key={ev.id}
              className="bg-white border border-amber-100 rounded-xl px-4 py-3 flex items-center gap-3 flex-wrap"
              data-testid={`due-harvest-${ev.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium text-stone-900 truncate">{ev.title}</div>
                <div className="text-xs text-stone-500">
                  {format(parseISO(ev.due_date), "EEE, MMM d, yyyy")}
                  {ev.hive_id ? ` · ${hiveName(ev.hive_id) || "hive"}` : " · any hive"}
                  {overdue && <span className="ml-2 text-orange-700">· overdue</span>}
                </div>
              </div>
              <Button
                size="sm"
                className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300"
                onClick={() => onPick(ev)}
                data-testid={`log-from-reminder-${ev.id}`}
              >
                Log harvest
              </Button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
